import graphClient from "../graphql/graph_client";
import { ImportGooglePlace } from "../graphql/poi-graph";

/**
 * Places on Google Maps, through our SerpApi proxy: searching for them, and
 * adding one that's picked to our own places (importGooglePlace mutation).
 * Shared by the planner's place search and the site-wide Explore search.
 */

/** A place found by searching Google Maps (through the SerpApi proxy). */
export interface GooglePlace {
  placeId: string;
  name: string;
  /** Google's own word for it: "Hotel", "Bed & breakfast"… */
  category: string | null;
  /** The closest of our POI types (see googlePlaceType). */
  type: string;
  address: string | null;
  rating: number | null;
  /** [lon, lat] */
  coordinates: [number, number];
}

// First match wins, so the narrower kinds of lodging come before "hotel".
const GOOGLE_TYPES: Array<[RegExp, string]> = [
  [/hostel/i, 'hostel'],
  [/bed (and|&) breakfast|b ?& ?b|guest ?house|\binn\b/i, 'bnb'],
  [/motel/i, 'motel'],
  [/camp(ground|site|ing)|caravan|glamping|rv park/i, 'campground'],
  [/hotel|resort|lodg|holiday (home|apartment)|cottage|chalet/i, 'hotel'],
  [/\bpub\b|\bbar\b|brewery/i, 'pub'],
  [/restaurant|bistro|diner/i, 'restaurant'],
  [/caf[eé]|coffee|tea ?room|bakery/i, 'coffee'],
  [/supermarket|grocery|shop|store/i, 'shop'],
  [/bus (stop|station)|train station|railway|transit/i, 'transport_station'],
  [/parking|car park/i, 'parking'],
];

/** The closest of our POI types for a Google place, from its category (or its other types). */
export function googlePlaceType(category: string | null | undefined, types: string[] = []): string {
  for (const text of [category, ...types]) {
    if (!text) continue;
    const match = GOOGLE_TYPES.find(([pattern]) => pattern.test(text));
    if (match) return match[1];
  }
  return 'other';
}

/** A coordinate as a number — given as one or as a numeric string; null for anything else (Number(null) would be 0). */
function coordinate(value: unknown): number | null {
  const number = typeof value === 'number' ? value : typeof value === 'string' && value.trim() !== '' ? Number(value) : NaN;
  return Number.isFinite(number) ? number : null;
}

/**
 * The places in a SerpApi Google Maps search response: its `local_results`,
 * or the one `place_results` when Google went straight to a single place.
 */
export function googlePlacesFromSerp(json: any): GooglePlace[] {
  const rows: any[] = Array.isArray(json?.local_results)
    ? json.local_results
    : json?.place_results ? [json.place_results] : [];
  return rows.flatMap(row => {
    const lat = coordinate(row?.gps_coordinates?.latitude);
    const lon = coordinate(row?.gps_coordinates?.longitude);
    const name = row?.title || row?.name;
    if (lat === null || lon === null || !name) return [];
    const category = typeof row.type === 'string' ? row.type : Array.isArray(row.type) ? row.type[0] ?? null : null;
    return [{
      placeId: String(row.place_id ?? row.data_id ?? `${lat},${lon}`),
      name,
      category,
      type: googlePlaceType(category, Array.isArray(row.types) ? row.types : []),
      address: row.address ?? null,
      rating: typeof row.rating === 'number' ? row.rating : null,
      coordinates: [lon, lat] as [number, number],
    }];
  });
}

/** Google results further than this from where the search is are somewhere else entirely. */
export const GOOGLE_RESULT_RANGE_M = 50000;

/**
 * The Google Maps results worth offering: those within range of `focus`,
 * nearest first. Google only leans towards the point it's given, so a
 * search can come back with a namesake on the other side of the country.
 */
export function rankGooglePlaces(places: GooglePlace[], focus: number[], limit: number = 8): GooglePlace[] {
  return places
    .map(place => ({ place, metres: metresBetween(focus, place.coordinates) }))
    .filter(entry => entry.metres <= GOOGLE_RESULT_RANGE_M)
    .sort((a, b) => a.metres - b.metres)
    .slice(0, limit)
    .map(entry => entry.place);
}

// Too common to tell one place from another.
const FILLER_WORDS = new Set(['the', 'and', 'at', 'of', 'in', 'on', 'by']);

// Lowercase, without accents or apostrophes (so "O'Connor's" is one word
// however it's typed), split at anything else that isn't a letter or digit.
function plainWords(text: string): string[] {
  return text
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/['’]/g, '')
    .split(/[^a-z0-9]+/)
    .filter(Boolean);
}

/**
 * Whether a place's name has every telling word of `query` in it — what
 * counts as our places already having the one being searched for, rather
 * than a search engine's near-miss ("Rooms at the Sportsman" isn't "The
 * Devil's Dressing Room").
 */
export function nameMatchesQuery(name: string | null | undefined, query: string): boolean {
  const wanted = plainWords(query).filter(word => word.length > 1 && !FILLER_WORDS.has(word));
  if (!name || wanted.length === 0) return false;
  // The whole name run together, so a word split differently ("O Connors")
  // still matches.
  const haystack = plainWords(name).join('');
  return wanted.every(word => haystack.includes(word));
}

/** Great-circle distance in metres between two [lon, lat] points. */
function metresBetween(a: number[], b: number[]): number {
  const toRad = (degrees: number) => (degrees * Math.PI) / 180;
  const dLat = toRad(b[1] - a[1]);
  const dLon = toRad(b[0] - a[0]);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a[1])) * Math.cos(toRad(b[1])) * Math.sin(dLon / 2) ** 2;
  return 6371000 * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
}

/**
 * Places on Google Maps matching `query` near `focus` ([lon, lat]), through
 * our SerpApi proxy. `zoom` is how closely Google looks around the focus.
 * Throws when the search can't be made.
 */
export async function searchGoogleMaps(query: string, focus: number[], zoom: number = 13): Promise<GooglePlace[]> {
  const params = new URLSearchParams({ q: query, ll: `@${focus[1]},${focus[0]},${zoom}z` });
  const response = await fetch(`/api/serp_api/search?${params.toString()}`);
  if (!response.ok) throw new Error(`Google Maps search failed (${response.status})`);
  return googlePlacesFromSerp(await response.json());
}

/** One of our places, as the importGooglePlace mutation returns it. */
export interface ImportedPlace {
  id: string;
  klass: 'Accommodation' | 'Amenity';
  type: string;
  name: string;
  /** [lon, lat] */
  coordinates: [number, number];
  /** Where its page is. */
  shareUrl: string | null;
}

/**
 * Adds a place picked from Google Maps to our own places (or finds the one we
 * already have), so what's made from it links to a real POI. Null when it
 * can't be: not signed in, too many added lately, a kind of place we don't
 * keep, or Google Maps unreachable.
 */
export async function importGooglePlace(placeId: string): Promise<ImportedPlace | null> {
  try {
    const data: any = await graphClient.request(ImportGooglePlace, { input: { placeId } });
    const poi = data?.importGooglePlace?.poi;
    const [lon, lat] = poi?.lonlat ?? [];
    if (!poi || (poi.klass !== 'Accommodation' && poi.klass !== 'Amenity') || !Number.isFinite(lon) || !Number.isFinite(lat)) return null;
    return { id: String(poi.id), klass: poi.klass, type: poi.type, name: poi.name, coordinates: [lon, lat], shareUrl: poi.shareUrl ?? null };
  } catch {
    return null;
  }
}
